import Swal from "sweetalert2";
import { getServerErrorStatus, getServerErrorMessage, getServerErrorCode } from "./utils";

const MANUAL_CLOSE = "MANUAL_CLOSE";

export const errorRetryAlert = ({ t, onRetry, onCancel, error, defaultError }) => {
  //no lo mostramos si hay uno visible por si estamos mostrando el diálogo de sesión expirada
  // if (!Swal.isVisible()) {
  queueAlert(
    {
      heightAuto: false,
      icon: "error",
      title: "Oops...",
      text: error ? getServerErrorMessage(t, error) : defaultError || t("errors.somethingWrong"),
      showCancelButton: true,
      confirmButtonColor: "#72bf44",
      confirmButtonText: t("actions.retry"),
      cancelButtonText: t("actions.cancel"),
    },
    (result) => {
      if (result.value) {
        return onRetry();
      }
      return onCancel ? onCancel() : null;
    }
  );
  // }
};

export const confirmAlertPromise = ({
  t,
  description,
  confirmText,
  cancelText,
  onConfirm,
  successMessage,
  onSuccess,
  ignoreResult,
  input,
  onFinish,
  inputAttributes,
  inputValue,
  inputErrorText,
  onError,
  errorCode,
  title,
  manageResponse,
  successTime,
  onCancel,
}) => {
  queueAlert(
    {
      heightAuto: false,
      title: title || t("dialog.areYouSure"),
      text: description,
      icon: "warning",
      input: input,
      inputAttributes: inputAttributes,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: confirmText,
      cancelButtonText: cancelText || t("actions.cancel"),
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      backdrop: true,
      allowEscapeKey: () => !Swal.isLoading(),
      inputValidator: (value) => {
        if (input && !value) {
          return t("errors.emailRequired");
        }
      },
      preConfirm: (response) => {
        if (inputValue && response !== inputValue) {
          Swal.showValidationMessage(inputErrorText || t("errors.somethingWrong"));
        } else {
          Swal.disableButtons();
          return onConfirm(response)
            .then((r) => {
              return r;
            })
            .catch((error) => {
              console.error("error: ", error);
              if (onError && (!errorCode || errorCode === getServerErrorCode(error))) {
                onError(error);
                Swal.close({ result: MANUAL_CLOSE });
              } else {
                Swal.update({
                  showConfirmButton: false,
                  cancelButtonText: t("actions.close"),
                });
                if (error && error.errorMessage) {
                  Swal.showValidationMessage(
                    error && error.errorMessage ? error.errorMessage : t("errors.unexpectedError")
                  );
                } else {
                  Swal.showValidationMessage(getServerErrorMessage(t, error));
                }
              }
            });
        }
      },
    },
    (result) => {
      if (!result || result.result !== MANUAL_CLOSE) {
        if (onFinish) onFinish();
        // console.log("result: ", result);
        if (!result.isDismissed && (result.value || ignoreResult)) {
          currentAlertisVisible = false;
          if (!manageResponse && successMessage) {
            queueAlert({
              heightAuto: false,
              title: successMessage,
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
          }
          if (onSuccess) {
            setTimeout(
              () => {
                onSuccess(result ? result.value : null);
              },
              manageResponse ? 0 : successTime || 1500
            );
          }
        } else if (result.isDismissed && onCancel) {
          onCancel();
        }
      }
    }
  );
};

export const confirmAlert = (props) => {
  const {
    t,
    title,
    description,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    timer,
    timerProgressBar,
    confirmByTimer,
    allowOutsideClick,
    allowEscapeKey,
  } = props;
  queueAlert(
    {
      heightAuto: false,
      title: title || t("dialog.areYouSure"),
      text: description,
      icon: "warning",
      showCancelButton: true,
      timer: timer,
      timerProgressBar: timerProgressBar,
      confirmButtonColor: "#d33",
      confirmButtonText: confirmText || t("actions.confirm"),
      cancelButtonText: cancelText || t("actions.cancel"),
      showLoaderOnConfirm: true,
      allowOutsideClick: allowOutsideClick || false,
      backdrop: true,
      allowEscapeKey: allowEscapeKey || false,
      didOpen: (toast) => {
        if (timer) {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        }
      },
      preConfirm: () => {
        Swal.disableButtons();
      },
    },
    (result) => {
      if (allowOutsideClick && result.isDismissed && result.dismiss === "backdrop") {
        //
      } else {
        if (confirmByTimer && result.isDismissed && result.dismiss === Swal.DismissReason.timer) {
          onConfirm();
        } else if (result.isDismissed && onCancel) {
          onCancel();
        } else if (result.isConfirmed) {
          onConfirm();
        }
      }
    }
  );
};

export const waitingAlert = (props) => {
  const { t, waitingFunction, onComplete, title, onError } = props;

  queueAlert({
    heightAuto: false,
    title: title || t("pleaseWait"),
    confirmButtonText: "",
    showConfirmButton: false,
    willOpen: () => {
      Swal.showLoading();
      return waitingFunction()
        .then((r) => {
          Swal.close();
          if (onComplete) {
            onComplete(r);
          }
        })
        .catch((error) => {
          console.error("error: ", error);
          currentAlertisVisible = false;
          queueAlert({
            heightAuto: false,
            title: getServerErrorMessage(t, error),
            icon: "error",
            timer: 2500,
            showConfirmButton: false,
          });
          if (onError) {
            onError(error);
          }
        })
        .finally(() => {
          Swal.hideLoading();
        });
    },
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
};

export const waitingAndSuccessAlert = ({
  t,
  title,
  waitingFunction,
  successMessage,
  onSuccess,
  onPreSuccess,
  onError,
  ignoreResult,
  successTime,
  errorTime,
  errorCode,
  manageResponse,
}) => {
  queueAlert(
    {
      heightAuto: false,
      title: title || t("pleaseWait"),
      confirmButtonText: "",
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
        return waitingFunction()
          .then((r) => {
            Swal.close({ result: r });
            return r;
          })
          .catch((error) => {
            console.error("ERROR: ", error);
            let errorMessage = getServerErrorMessage(t, error);
            currentAlertisVisible = false;
            // if (onError && (!errorCode || errorCode === getServerErrorCode(error))) {
            //   console.log("ERROR CODE: ", errorCode);
            //   onError(error);
            // } else {
            queueAlert({
              heightAuto: false,
              title: errorMessage,
              icon: "error",
              timer: errorTime || 1500,
              showConfirmButton: false,
            });
            // }
            if (onError) {
              onError();
            }
          });
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
    },
    (result) => {
      if (ignoreResult || (result && (result.id || Array.isArray(result)) && successMessage)) {
        currentAlertisVisible = false;
        if (!manageResponse) {
          queueAlert({
            heightAuto: false,
            title: successMessage,
            icon: "success",
            timer: successTime || 1500,
            showConfirmButton: false,
          });
        }
        if (onPreSuccess) {
          onPreSuccess(result.result);
        }
        if (onSuccess) {
          setTimeout(
            () => {
              onSuccess(result.result);
            },
            manageResponse ? 0 : successTime || 1500
          );
        }
      }
    }
  );
};

export const successAlert = ({ t, successMessage, successTime }) => {
  //no lo mostramos si hay uno visible por si estamos mostrando el diálogo de sesión expirada
  // if (!Swal.isVisible()) {
  queueAlert({
    heightAuto: false,
    title: successMessage,
    icon: "success",
    timer: successTime || 1500,
    showConfirmButton: false,
  });
  // }
};
export const errorAlert = ({ t, error }) => {
  //no lo mostramos si hay uno visible por si estamos mostrando el diálogo de sesión expirada
  if (!Swal.isVisible()) {
    queueAlert({
      heightAuto: false,
      icon: "error",
      title: "Oops...",
      text: error ? getServerErrorMessage(t, error) : t("errors.somethingWrong"),
      showCancelButton: false,
      confirmButtonColor: "#72bf44",
      confirmButtonText: t("actions.accept"),
    });
  }
};

export const adviceAlert = ({ t, text, onComplete, title }) => {
  queueAlert(
    {
      heightAuto: false,
      icon: "warning",
      title: title || t ? t("attention") : "Oops...",
      text: text,
      showCancelButton: false,
      confirmButtonColor: "#72bf44",
      confirmButtonText: t("actions.accept"),
      allowOutsideClick: false,
    },
    () => {
      if (onComplete) {
        onComplete();
      }
    }
  );
};

export const towOptionsAlert = ({ text, title, onOption1, onOption2, textOption1, textOption2 }) => {
  queueAlert(
    {
      heightAuto: false,
      icon: "info",
      title: title,
      text: text,
      showCancelButton: true,
      confirmButtonColor: "#72bf44",
      cancelButtonColor: "#ff5722",
      confirmButtonText: textOption1,
      cancelButtonText: textOption2,
    },
    (result) => {
      if (result.dismiss === "cancel") {
        if (onOption2) {
          onOption2();
        }
      } else if (result.value) {
        onOption1(1);
      }
    }
  );
};

export const infoAlert = ({ t, text, onComplete, title }) => {
  queueAlert(
    {
      heightAuto: false,
      icon: "info",
      title: title || t("information"),
      text: text,
      showCancelButton: false,
      confirmButtonColor: "#72bf44",
      confirmButtonText: t("actions.accept"),
    },
    () => {
      if (onComplete) {
        onComplete();
      }
    }
  );
};

let alertQueue = [];

const queueAlert = (props, cb) => {
  alertQueue.push({ props: props, cb: cb });
  checkAlertQueue();
};
let currentAlertisVisible = false;

const SwalMixin = Swal.mixin({
  willClose: () => {
    currentAlertisVisible = false;
    checkAlertQueue();
  },
});

const checkAlertQueue = async () => {
  // if (Swal.isVisible()) {
  //   console.log("VISIBLE, ESPERAMOS");
  //   setTimeout(() => {
  //     confirmAlert(props);
  //   }, 10);
  // } else {
  //   console.log("DISPARAMOS");
  //   fire();
  // }
  if (!currentAlertisVisible && alertQueue.length) {
    let nextAlert = alertQueue.shift();
    currentAlertisVisible = true;
    await SwalMixin.fire(nextAlert.props).then((result) => {
      if (nextAlert.cb) {
        nextAlert.cb(result);
      }
    });
  }
};

export const confirmAlertOverwriteDataPromise = (
  t,
  description,
  confirmText,
  cancelText,
  onConfirm,
  successMessage,
  onSuccess,
  timer,
  noResp
) => {
  Swal.fire({
    heightAuto: false,
    title: t("dialog.areYouSure"),
    text: description,
    icon: "warning",
    showCancelButton: true,

    confirmButtonColor: "#d33",
    confirmButtonText: confirmText,
    cancelButtonText: cancelText || t("actions.cancel"),
    html: description,
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    allowEscapeKey: () => !Swal.isLoading(),
    preConfirm: () => {
      Swal.disableButtons();
      return onConfirm()
        .then((r) => {
          return r;
        })
        .catch((error) => {
          console.error("error: ", error);
          if (error && error.errorMessage) {
            Swal.showValidationMessage(error && error.errorMessage ? error.errorMessage : t("errors.unexpectedError"));
          } else {
            Swal.showValidationMessage(getServerErrorMessage(t, error));
          }
        });
    },
  }).then((result) => {
    if (!result.dismiss && (result.value || noResp) && successMessage) {
      Swal.fire({
        heightAuto: false,
        title: successMessage,
        icon: "success",
        timer: timer || 1500,
        showConfirmButton: false,
      });
      if (onSuccess) {
        setTimeout(() => {
          onSuccess(result ? result.value : null);
        }, 1500);
      }
    }
  });
};
